import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  MIN_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { OwnerColumnFilter } from '@issuemanager/client/features/issues';
import { useInfiniteUsers } from '@issuemanager/client/features/users';
import { useState } from 'react';
import type { GroupedUserDto, MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type OwnerMultipleSelectorProps = MultipleSelectorProps<
  GroupedUserDto[],
  GroupedUserDto
>;

export function OwnerMultipleSelector(props: OwnerMultipleSelectorProps) {
  const { value, onChange, enabled: _enabled, ...rest } = props;

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value ?? []);
  const issueOwnersQuery = useInfiniteUsers({
    filter: {
      searchTerm: filterByText,
      isIssueOwner: true,
    },
    enabled: filterByText.length >= MIN_CHARACTERS_TO_SEARCH,
    handleError: true,
  });
  const actionOwnersQuery = useInfiniteUsers({
    filter: {
      searchTerm: filterByText,
      isActionOwner: true,
    },
    enabled: filterByText.length >= MIN_CHARACTERS_TO_SEARCH,
    handleError: true,
  });

  const issueOwners =
    issueOwnersQuery.data?.pages.map((c) =>
      c.data.map((d) => ({ ...d, group: OwnerColumnFilter.ISSUE_OWNER })),
    ) ?? [];

  const actionOwners =
    actionOwnersQuery.data?.pages.map((c) =>
      c.data.map((d) => ({ ...d, group: OwnerColumnFilter.ACTION_OWNER })),
    ) ?? [];

  const options = mergePaginatedDataAndValue(
    issueOwners.concat(actionOwners),
    value ?? [],
    (option) => `${option.id}-${option.group}`,
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    loading: issueOwnersQuery.isLoading || actionOwnersQuery.isLoading,
    onScrollEnd: () => {
      issueOwnersQuery.fetchNextPage();
      actionOwnersQuery.fetchNextPage();
    },
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
    isOptionEqualToValue: (option, value) =>
      option.id === value.id && option.group === value.group,
  });

  const sortedOptions = sortOptionsBySelected(
    options,
    partialValue,
    autocompleteProps.isOptionEqualToValue,
  );

  const finalOptions = sortedOptions
    .filter((o) => o.group === OwnerColumnFilter.ISSUE_OWNER)
    .concat(
      sortedOptions.filter((o) => o.group === OwnerColumnFilter.ACTION_OWNER),
    );

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={finalOptions}
      getOptionKey={(option) => `${option.id}-${option.group}`}
      getOptionLabel={(option) => `${option.name} <${option.email}>`}
      groupBy={(option) => option.group}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
